import React, { useContext } from "react";
import PropTypes from "prop-types";
import ItemQuantitySelect from "./ItemQuantitySelect";
import { formatCheckoutPrice } from "~/assets/js/Format";
import i18n from "~/assets/js/Helpers/i18n";
import { CheckoutContext } from "../../pages/Checkout/CheckoutProvider";
import OverlayLoader from "~/assets/js/components/Loaders/OverlayLoader";
import ClimateDetails from "~/assets/js/components/ClimateDetails";
import { AddToast } from "~/assets/js/components/Toast";
import { useCartContext } from "~/assets/js/global/cartContext";
import { isMainDish } from "~/assets/js/Helpers/items";
import Decimal from "decimal.js";

const CartItem = ({ cartItem, inCartPosition, readOnly = false, items }) => {
    const { cartLoading, cartError, actions } = useCartContext();
    const { dispatch } = useContext(CheckoutContext);
    const { reusableBowlsId } = reactConfig;
    const ingredients = cartItem.add;
    const removedIngredients = cartItem.remove;

    const removeItemFromCart = async () => {
        let includesReusableBowls = false;
        const countReusable = items.reduce((totalReusables, item) => {
            if (item.product_id === reusableBowlsId) {
                includesReusableBowls = true;
            }
            return isMainDish(item) ? totalReusables + 1 : totalReusables;
        }, 0);

        const isLastSaladOrBowl = countReusable === 1 && includesReusableBowls;
        if (isLastSaladOrBowl) {
            await actions.removeMultipleItemsFromCartById([
                reusableBowlsId,
                cartItem.product_id,
            ]);
        } else {
            await actions.removeItemFromCart(inCartPosition);
        }

        if (cartError) {
            return dispatch(
                AddToast(
                    "danger",
                    i18n.t("toasts.remove_product.title_error", {
                        name: cartItem.name,
                    }),
                    "",
                    () => removeItemFromCart(inCartPosition)
                )
            );
        }

        return dispatch(
            AddToast(
                "success",
                i18n.t("toasts.remove_product.title_success", {
                    name: cartItem.name,
                })
            )
        );
    };

    const updateItemFromCart = async (quantity) => {
        const prevQuantity = cartItem.quantity;
        await actions.updateItemQuantity(inCartPosition, quantity);

        if (cartError) {
            return dispatch(
                AddToast(
                    "danger",
                    i18n.t("toasts.update_quantity.title_error", {
                        name: cartItem.name,
                    }),
                    "",
                    () => updateItemFromCart(inCartPosition, quantity)
                )
            );
        }
        return dispatch(
            AddToast(
                "success",
                i18n.t("toasts.update_quantity.title_success", {
                    name: cartItem.name,
                    prevQ: prevQuantity,
                    currentQ: quantity,
                })
            )
        );
    };

    // Item data
    const { comment, co2, water, calculated_energy } = cartItem;
    const hasIngredients = !!cartItem.add?.length;
    const hasRemovedIngredients = !!removedIngredients?.length;

    const renderIngredient = (ingredient) =>
        `${ingredient.quantity * cartItem.quantity} x ${ingredient.name}`;

    // Hide water and CO2 compensation
    const isEnvCompensationProduct = false; //Get compensation products from cart
    if (isEnvCompensationProduct) {
        return null;
    }
    if (cartItem.category === "MENU") {
        return null;
    }
    const menuAddons = items?.filter((item) => {
        return (
            item?.menu_uid === cartItem?.menu_uid && item?.category === "MENU"
        );
    });
    const menuAddon = { add: [], value_gross: 0 };
    menuAddons?.forEach((addon) => {
        menuAddon["add"].push(...addon["add"]);
        menuAddon["value_gross"] += parseFloat(addon["value_gross"]);
        menuAddon["quantity"] = addon["quantity"];
    });

    return (
        <div className="cart-item-card">
            {cartLoading && <OverlayLoader />}
            <div className="cart-item-card__image-container">
                {!!cartItem.thumbnail_url && (
                    <a href={cartItem.url}>
                        <img
                            className="cart-item-card__image"
                            src={cartItem.thumbnail_url}
                            //srcSet={cartItem.images[0].srcset}
                            alt="product image"
                        />
                    </a>
                )}
            </div>
            <div className="cart-item-card__data-container">
                <a href={cartItem.url}>
                    <span className="cart-item-card__heading">
                        {readOnly ? `${cartItem.quantity}x ` : ""}
                        {cartItem.name}
                    </span>
                </a>
                <span className="cart-item-card__price">
                    {!new Decimal(cartItem.pre_discount_value_gross).equals(
                        new Decimal(cartItem.value_gross)
                    ) && (
                        <span
                            style={{
                                textDecoration: "line-through",
                                paddingRight: "10px",
                            }}>
                            {formatCheckoutPrice(
                                cartItem.pre_discount_value_gross,
                                0,
                                "€"
                            )}
                        </span>
                    )}
                    {formatCheckoutPrice(cartItem.value_gross, 0, "€")}{" "}
                    {menuAddon.value_gross > 0 && (
                        <>
                            +{" "}
                            {formatCheckoutPrice(
                                menuAddon.value_gross.toString(),
                                0,
                                "€"
                            )}
                        </>
                    )}
                </span>

                <ClimateDetails
                    climateDetails={{
                        co2,
                        water,
                        calculated_energy,
                    }}
                    itemQuantity={cartItem.quantity}
                />

                <div className="cart-item-card__additions">
                    {hasIngredients &&
                        ingredients.map((ingredient) => {
                            return (
                                <p
                                    key={ingredient.name}
                                    className="cart-item-card__additions__entry">
                                    {renderIngredient(ingredient)}
                                </p>
                            );
                        })}
                    {hasRemovedIngredients &&
                        removedIngredients.map((ingredient) => {
                            return (
                                <p
                                    key={ingredient.name}
                                    className="cart-item-card__additions__entry">
                                    {"ohne: " + ingredient.name}
                                </p>
                            );
                        })}
                    {menuAddon.add.length > 0 && (
                        <div className="cart-item-card__additions__entry">
                            {menuAddon.quantity} x Spar-Menü Upgrade:
                            <div className="cart-item-card__additions__menu-items">
                                {menuAddon.add.map((adds, index) => {
                                    return (
                                        <p
                                            className="cart-item-card__additions__entry"
                                            key={index}>
                                            {adds.name} <br />
                                        </p>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {!!comment && (
                        <p className="cart-item-card__additions__entry">
                            {comment}
                        </p>
                    )}
                </div>
                {!readOnly && (
                    <div className="cart-item-card__actions">
                        <div className="cart-item-card__actions__quantity">
                            <ItemQuantitySelect
                                maxQuantity={20}
                                defaultOption={cartItem.quantity}
                                itemKey={cartItem.product_id}
                                updateCart={updateItemFromCart}
                            />
                        </div>
                        <a
                            onClick={() =>
                                removeItemFromCart(cartItem.product_id)
                            }
                            className="cart-item-card__actions__remove-item">
                            <i className="fa fa-times-circle"></i>
                            {i18n.t("cart.cartItem.remove")}
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
};

CartItem.propTypes = {
    cartItem: PropTypes.object.isRequired,
    readOnly: PropTypes.bool,
    inCartPosition: PropTypes.number.isRequired,
    items: PropTypes.array,
};

export default CartItem;
